import { useMemo, useState } from "react";

/** Mode tablette plein écran — pesée au bord du tapis en 2 touches : (1) toucher l'athlète, (2) poids au clavier géant → Enregistrer. */
export const WeighInKiosk = ({ rows, onSave, onClose }) => {
  const [sel, setSel] = useState(null);
  const [val, setVal] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = useMemo(() => rows.filter((r) => r.official_kg == null), [rows]);
  const list = (q ? rows : pending).filter((r) => !q || (r.athlete_full_name || "").toLowerCase().includes(q.toLowerCase()));
  const pick = (r) => { setSel(r); setVal(r.official_kg != null ? String(r.official_kg) : r.declared_kg ? String(r.declared_kg) : ""); };
  const key = (k) => {
    if (k === "⌫") return setVal((v) => v.slice(0, -1));
    if (k === "," && val.includes(".")) return;
    setVal((v) => (v + (k === "," ? "." : k)).slice(0, 5));
  };
  const save = async () => {
    const w = Number(val);
    if (!sel || !(w >= 10 && w <= 250)) return;
    setBusy(true);
    try {
      const row = await onSave(sel, w);
      if (row) {
        setSel(null); setVal(""); setQ("");
      }
    } finally { setBusy(false); }
  };
  const w = Number(val);
  const delta = sel?.declared_kg && w ? (((w - sel.declared_kg) / sel.declared_kg) * 100).toFixed(1) : null;
  return (
    <div className="fixed inset-0 z-[80] bg-zinc-950 text-zinc-100 flex flex-col select-none" data-testid="weigh-kiosk">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-zinc-800">
        <div className="text-amber-400 text-[10px] tracking-[0.4em]">PESÉE OFFICIELLE · MODE TABLETTE</div>
        <div className="text-sm text-zinc-400" data-testid="weigh-kiosk-progress">{rows.length - pending.length}/{rows.length} pesés</div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chercher un nom…" className="ml-auto bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2 text-base w-64" data-testid="weigh-kiosk-search" />
        <button onClick={onClose} className="px-4 py-2 rounded-xl border border-zinc-700 text-zinc-300 text-sm hover:border-red-500/60" data-testid="weigh-kiosk-close">✕ Quitter</button>
      </div>
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_380px] min-h-0">
        <div className="overflow-y-auto p-4 grid grid-cols-2 lg:grid-cols-3 gap-3 content-start">
          {list.map((r) => (
            <button key={r.id} onClick={() => pick(r)} className={`text-left rounded-2xl border p-4 min-h-[92px] active:scale-[0.98] transition-transform ${sel?.id === r.id ? "border-amber-400 bg-amber-500/10" : r.official_kg != null ? "border-emerald-700/50 bg-emerald-900/10" : "border-zinc-700 bg-zinc-900/60"}`} data-testid={`weigh-kiosk-athlete-${r.id}`}>
              <div className="text-lg font-bold leading-tight">{r.athlete_full_name}</div>
              <div className="text-xs text-zinc-400 mt-1">{r.age} ans · {r.school_name || "—"} · déclaré {r.declared_kg ? `${r.declared_kg} kg` : "—"}</div>
              {r.official_kg != null && <div className={`text-xs mt-1 ${r.discrepancy ? "text-red-300" : "text-emerald-300"}`}>{r.official_kg} kg · {r.status}</div>}
            </button>
          ))}
          {list.length === 0 && <div className="col-span-full text-zinc-500 text-center py-10 text-lg" data-testid="weigh-kiosk-empty">{q ? "Aucun athlète trouvé" : "Tous les athlètes sont pesés ✔"}</div>}
        </div>
        <div className="border-t md:border-t-0 md:border-l border-zinc-800 p-5 flex flex-col gap-3">
          <div className="min-h-[56px]">
            {sel ? (
              <>
                <div className="text-xl font-black leading-tight" data-testid="weigh-kiosk-selected">{sel.athlete_full_name}</div>
                <div className="text-xs text-zinc-400">{sel.entries.map((e) => e.declared_class || "—").join(" / ")} · déclaré {sel.declared_kg ? `${sel.declared_kg} kg` : "—"}</div>
              </>
            ) : <div className="text-zinc-500 text-base">1 · Touche un athlète à gauche</div>}
          </div>
          <div className="rounded-2xl bg-zinc-900 border border-zinc-700 px-4 py-3 text-right">
            <span className="text-5xl font-black tabular-nums" data-testid="weigh-kiosk-value">{val || "0"}</span><span className="text-xl text-zinc-400 ml-2">kg</span>
            {delta !== null && <div className={`text-xs mt-1 ${Math.abs(delta) > 3 ? "text-amber-300" : "text-zinc-500"}`}>écart {delta > 0 ? "+" : ""}{delta} % vs déclaré</div>}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {["7", "8", "9", "4", "5", "6", "1", "2", "3", ",", "0", "⌫"].map((k) => (
              <button key={k} onClick={() => key(k)} disabled={!sel} className="h-16 rounded-2xl bg-zinc-800 hover:bg-zinc-700 disabled:opacity-30 text-2xl font-bold active:scale-95 transition-transform" data-testid={`weigh-kiosk-key-${k === "⌫" ? "del" : k === "," ? "dot" : k}`}>{k}</button>
            ))}
          </div>
          <button onClick={save} disabled={!sel || busy || !(w >= 10 && w <= 250)} className="h-16 rounded-2xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-30 text-xl font-black active:scale-[0.98] transition-transform" data-testid="weigh-kiosk-save">
            2 · Enregistrer {w >= 10 ? `${w} kg` : ""}
          </button>
        </div>
      </div>
    </div>
  );
};
